import React from 'react';
import PropTypes from 'prop-types';
import { createRefetchContainer, graphql } from 'react-relay';
import { Translate } from '@hydra/common/components';
import get from 'lodash/get';

import CrawlerTable from '@hydra/routes/crawler/components/CrawlerTable';
import NonIndexablePagesRow from '../NonIndexablePagesRow';

import FilterContainer from '@hydra/routes/crawler/components/FilterContainer';
import FilterCheckboxes from '@hydra/routes/crawler/components/FilterCheckboxes';
import FilterString from '@hydra/routes/crawler/components/FilterString';
import { withFilters } from '@hydra/routes/crawler/components/withFilters';

const SITEMAP_FILTERS = [
  {
    identifier: 'showInSitemap',
    value: 'showInSitemap'
  }, {
    identifier: 'showNotInSitemap',
    value: 'showNotInSitemap'
  }
];

export class NonIndexablePagesTable extends React.Component {
  static propTypes = {
    relay: PropTypes.object,
    project: PropTypes.shape({
      crawl: PropTypes.shape({
        indexingCategory: PropTypes.shape({
          countNoindexTypesNoindexedPages: PropTypes.arrayOf(
            PropTypes.shape({
              count: PropTypes.number,
              type: PropTypes.string
            })
          ),
          noindexedPages: PropTypes.shape({
            count: PropTypes.number,
            edges: PropTypes.arrayOf(
              PropTypes.shape({
                node: PropTypes.object
              })
            )
          })
        })
      })
    }).isRequired,
    offset: PropTypes.number,
    limit: PropTypes.number
  }

  render () {
    const noIndexToggles = get(this.props.project, 'crawls.edges[0].node.countNoindexTypesNoindexedPages', [])
      .map(v => ({ identifier: v.type, ...v }));
    return (
      <>
        <FilterContainer>
          <FilterString
            onChange={this.props.onGraphQLInputChange('url')}
            value={this.props.filters.url}
            name='url'
            label={<Translate content='crawler_non_indexable_page_url' />}
          />

          <FilterString
            onChange={this.props.onGraphQLInputChange('sourceUrl')}
            value={this.props.filters.sourceUrl}
            name='sourceUrl'
            label={<Translate content='crawler_source_page_url' />}
          />

          <FilterCheckboxes
            onChange={this.props.onGraphQLCheckboxInputChange('noindexTypeFilters', noIndexToggles)}
            checkedItems={this.props.filters.noindexTypeFilters}
            groups={noIndexToggles}
            name='noindexTypeFilters'
            label={<Translate content='crawler_noindex_type' />}
          />

          <FilterCheckboxes
            onChange={this.props.onGraphQLCheckboxInputChange('sitemapFilter', SITEMAP_FILTERS, true)}
            checkedItems={this.props.filters.sitemapFilter}
            groups={SITEMAP_FILTERS}
            name='sitemapFilter'
            label={<Translate content='crawler_linking_sitemap_check' />}
          />
        </FilterContainer>
        <CrawlerTable {...this.props} category='indexingCategory' section='noindexedPages'>
          <CrawlerTable.TableHead>
            <Translate content='crawler_non_indexable_page_url' size='5' />
            <Translate content='crawler_source_page_url' size='4' />
            <Translate content='crawler_noindex_type' size='2' />
            <Translate content='crawler_linking_sitemap_check' size='1' />
          </CrawlerTable.TableHead>
          <CrawlerTable.Body component={NonIndexablePagesRow} />
        </CrawlerTable>
      </>
    );
  }
}

const WithFiltersTable = (withFilters)(NonIndexablePagesTable);

export default createRefetchContainer(
  WithFiltersTable,
  {
    project: graphql`
      fragment NonIndexablePagesTable_project on Project
      @argumentDefinitions(
        offset: {type: "Int", defaultValue: 0},
        limit: {type: "Int", defaultValue: 20},
        url: {type: "TextFilters", defaultValue: { operator: AND, values: [] }},
        sourceUrl: {type: "TextFilters", defaultValue: { operator: AND, values: [] }},
        noindexTypeFilters: {type: "[String]", defaultValue: []},
        sitemapFilter: {
          type: "SitemapFilter",
          defaultValue: { showInSitemap: true, showNotInSitemap: true }
        }
      ) {
        host,
        crawls (offset: 0, limit: 1) {
          edges {
            node {
              countNoindexTypesNoindexedPages(sitemapFilter: $sitemapFilter, url: $url, sourceUrl: $sourceUrl) {
                type
                count
              }
              noindexedPages(offset: $offset, limit: $limit, url: $url, sourceUrl: $sourceUrl, noindexTypeFilters: $noindexTypeFilters, sitemapFilter: $sitemapFilter) {
                count
                edges {
                  node {
                    ...NonIndexablePagesRow_issue
                  }
                }
              }
            }
          }
        }
      }
    `
  },
  graphql`
      query NonIndexablePagesTable_RefetchQuery(
        $host: String!
        $limit: Int,
        $offset: Int,
        $sitemapFilter: SitemapFilter,
        $noindexTypeFilters: [String],
        $sourceUrl: TextFilters,
        $url: TextFilters) {,
        project (host: $host) {
          ...NonIndexablePagesTable_project @arguments(
            offset: $offset,
            limit: $limit,
            sitemapFilter: $sitemapFilter,
            noindexTypeFilters: $noindexTypeFilters,
            url: $url,
            sourceUrl: $sourceUrl
          )
        }
      }
    `
);
